import { defineStore } from "pinia";
import axios from "axios";

const STORAGE_KEYS = {
  USER: "user",
  WP_NONCE: "wpNonce",
  REMEMBERED_USER: "rememberedUser",
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null, // Stores the authenticated user's data
    wpNonce: "", // Stores the WordPress nonce for secure API requests
    status: {
      error: "",   // Holds error messages related to authentication actions
      success: "", // Holds success messages related to authentication actions
    },
    isLoading: false, // Indicates whether an authentication-related request is in progress
  }),

  actions: {
    /**
     * Sets the WordPress nonce, updates axios default headers, and stores the nonce.
     * @param {string} nonce - The nonce value to set.
     */
    setWpNonce(nonce) {
      this.wpNonce = nonce;
      axios.defaults.headers.common["X-WP-Nonce"] = nonce;
      if (window.wpData) {
        window.wpData.nonce = nonce;
      }
      this.getStorage().setItem(STORAGE_KEYS.WP_NONCE, nonce);
    },

    /**
     * Initializes authentication by setting the base URL, retrieving stored user data, and fetching user info if necessary.
     */
    async initAuth() {
      axios.defaults.baseURL = window.wpData?.root || "/wp-json/";

      const storage = this.getStorage();
      const storedUser = storage.getItem(STORAGE_KEYS.USER);
      const storedNonce = storage.getItem(STORAGE_KEYS.WP_NONCE);

      if (window.wpData) {
        this.setWpNonce(window.wpData.nonce);
        if (storedUser) {
          this.setUser(JSON.parse(storedUser), true);
        } else if (window.wpData.isLoggedIn && window.wpData.user) {
          await this.fetchLoggedInUser();
        }
      } else if (storedUser && storedNonce) {
        this.setUser(JSON.parse(storedUser), true);
        this.setWpNonce(storedNonce);
      }
    },

    /**
     * Fetches the currently logged-in user's data from the server.
     * @returns {boolean} - Returns true if the user data was successfully fetched, else false.
     */
    async fetchLoggedInUser() {
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";

      try {
        const response = await axios.get("my-plugin/v1/current-user", {
          withCredentials: true,
          headers: { "X-WP-Nonce": this.wpNonce },
        });

        if (response.data && response.data.success && response.data.user) {
          this.setUser(response.data.user, true);
          this.setWpNonce(response.data.nonce);
          return true;
        } else {
          this.status.error = response.data.message || "Kunne ikke hente brugerdata. Prøv venligst igen.";
          return false;
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        this.handleAxiosError(error, "An error occurred while fetching user data.");
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Sets the user data in the store and saves it to storage.
     * @param {Object} userData - The user data to set.
     * @param {boolean} rememberMe - Determines if the user data should be persisted across sessions.
     */
    setUser(userData, rememberMe = false) {
      this.user = {
        ...userData,
        events: userData.events || [],
        holiday_balances: userData.holiday_balances || {},
      };
      // Clear any existing success message
      this.status.success = "";
      this.status.error = "";
      this.saveUserData(rememberMe);
    },

    /**
     * Saves the user data and nonce to the appropriate storage (localStorage or sessionStorage).
     * If not remembering the user, removes data from localStorage.
     * @param {boolean} rememberMe - Determines if the user data should be persisted across sessions.
     */
    saveUserData(rememberMe) {
      const storage = this.getStorage();
      storage.setItem(STORAGE_KEYS.USER, JSON.stringify(this.user));
      storage.setItem(STORAGE_KEYS.WP_NONCE, this.wpNonce);
      if (!rememberMe) {
        // Remove data from localStorage to use sessionStorage instead
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.WP_NONCE);
      }
    },

    /**
     * Determines which storage (localStorage or sessionStorage) to use based on the presence of user data in localStorage.
     * @returns {Storage} - The chosen storage object.
     */
    getStorage() {
      return localStorage.getItem(STORAGE_KEYS.USER) ? localStorage : sessionStorage;
    },

    /**
     * Attempts to log in the user with provided credentials.
     * On success, stores user data and reloads the page to reflect the authenticated state.
     * @param {string} login - The user's login identifier.
     * @param {string} password - The user's password.
     * @param {boolean} rememberMe - Whether to remember the user across sessions.
     * @returns {boolean} - Returns true if login was successful, else false.
     */
    async login(login, password, rememberMe) {
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";

      try {
        const response = await axios.post(
          "my-plugin/v1/login",
          { login, password },
          { withCredentials: true }
        );

        if (response.data.success) {
          this.setUser(response.data.user, rememberMe);
          this.setWpNonce(response.data.nonce);
          localStorage.setItem(
            STORAGE_KEYS.REMEMBERED_USER,
            JSON.stringify({ username: login, rememberMe })
          );
          window.location.reload(); // Reload to update the application state
          return true;
        } else {
          this.status.error = "Login fejlede. Kontroller venligst dine loginoplysninger.";
          return false;
        }
      } catch (error) {
        this.handleAxiosError(error, "Login fejlede. Prøv venligst igen.");
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Logs out the currently authenticated user.
     * On success, clears user data and reloads the page to reflect the unauthenticated state.
     * @returns {boolean} - Returns true if logout was successful, else false.
     */
    async logout() {
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";

      try {
        const response = await axios.post(
          "my-plugin/v1/logout",
          {},
          {
            withCredentials: true,
            headers: { "X-WP-Nonce": this.wpNonce },
          }
        );

        if (response.data.success) {
          this.clearUserData();
          window.location.reload(); // Reload to update the application state
          return true;
        } else {
          this.status.error = "Logout fejlede. Prøv venligst igen.";
          return false;
        }
      } catch (error) {
        this.handleAxiosError(error, "Logout fejlede. Prøv venligst igen.");
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Clears all user-related data from the store and both storage mechanisms.
     */
    clearUserData() {
      this.user = null;
      this.wpNonce = "";
      axios.defaults.headers.common["X-WP-Nonce"] = "";
      // Remove all related storage keys from both localStorage and sessionStorage
      [localStorage, sessionStorage].forEach((storage) => {
        Object.values(STORAGE_KEYS).forEach((key) => storage.removeItem(key));
      });
    },

    /**
     * Handles Axios errors by setting appropriate error messages in the store.
     * @param {Object} error - The error object returned by Axios.
     * @param {string} defaultMessage - The default message to display if no specific message is available.
     */
    handleAxiosError(error, defaultMessage) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with a status other than 2xx
          this.status.error = error.response.data.message || defaultMessage;
        } else if (error.request) {
          // No response was received from the server
          this.status.error = "Ingen respons fra serveren. Kontroller din internetforbindelse.";
        } else {
          // Error occurred while setting up the request
          this.status.error = "Der opstod en fejl ved forberedelse af anmodningen.";
        }
      } else {
        // Non-Axios error occurred
        this.status.error = error.message || "Der opstod en uventet fejl.";
      }
    },
  },

  getters: {
    /**
     * Determines if a user is currently authenticated.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if the user is authenticated, else false.
     */
    isAuthenticated: (state) => !!state.user,

    /**
     * Retrieves the authenticated user's events.
     * @param {Object} state - The store's state.
     * @returns {Array} - Array of user events.
     */
    userEvents: (state) => state.user?.events || [],

    /**
     * Retrieves the authenticated user's holiday balances.
     * @param {Object} state - The store's state.
     * @returns {Object} - Object containing holiday balances.
     */
    userHolidayBalances: (state) => state.user?.holiday_balances || {},

    /**
     * Checks if the authenticated user has the 'administrator' role.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if the user is an administrator, else false.
     */
    isAdmin: (state) => (state.user?.roles && state.user?.roles.includes("administrator")) ?? false,
  },
});
